import { QueryFactory } from '@lmt/shared-types';

import { SettingsBlock } from '@lmt/unicorn-shared/app/components/common/ui-kit/configuration/SettingsBlock';

import {
  Button,
  ButtonColor,
} from '@lmt/unicorn-shared/app/components/common/ui-kit/buttons/Button';
import { SubmitButton } from '@lmt/unicorn-shared/app/components/common/ui-kit/buttons/SubmitButton';
import { CheckBox } from '@lmt/unicorn-shared/app/components/common/ui-kit/CheckBox';
import { FormComponentProps } from '@lmt/unicorn-shared/app/components/global-forms/GlobalForms.types';
import { PatientListFilterSettings } from '@lmt/unicorn-shared/app/components/patients/list/configuration/PatientListFilterSettings';
import { PatientListGeneralSettings } from '@lmt/unicorn-shared/app/components/patients/list/configuration/PatientListGeneralSettings';
import {
  defaultPatientListConfigurationData,
  PatientListConfigurationData,
} from '@lmt/unicorn-shared/app/components/patients/list/common/types';
import { useClaims } from '@lmt/unicorn-shared/app/features/auth/operations';
import { useUniDispatch } from '@lmt/unicorn-shared/app/store/types';
import { useModal } from '@lmt/unicorn-shared/app/components/common/ui-kit/modal/useModal';
import {
  handleSubmitFormError,
  useErrorHandler,
} from '@lmt/unicorn-shared/app/helpers/form/useErrorHandler';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useQueryClient } from '@tanstack/react-query';
import { convertToErrorString } from '@lmt/unicorn-shared/app/helpers/form/useErrorHandler';
import { useTranslation } from 'react-i18next';

import styles from './CreatePatientListConfiguration.module.scss';
import TrashIcon from '@lmt/unicorn-shared/app/icons/trash.svg?react';
import ResetIcon from '@lmt/unicorn-shared/app/icons/reset.svg?react';

interface Props {
  // id of configuration to edit (if existing configuration is being edited)
  configurationId?: number;
  values?: Partial<PatientListConfigurationData>;
}

export const CreatePatientListConfiguration: React.FC<
  FormComponentProps & Props
> = (props) => {
  const claims = useClaims();
  const dispatch = useUniDispatch();
  const modals = useModal();
  const i18n = useScopedTranslation('patient_list_configuration');
  const i18nGlobal = useTranslation();
  const queryClient = useQueryClient();

  const {
    formType,
    formId,
    tryCompleteForm,
    loadBlocking,
    configurationId,
    values,
  } = props;

  const configurationQuery =
    QueryFactory.PatientListConfigurationQuery.useGetPatientListConfigurationQuery(
      configurationId!,
      {
        enabled: !!configurationId,
      },
    );

  const formMethods = useForm<PatientListConfigurationData>({
    //resolver: yupResolver(patientListConfigurationDataYupSchema) as any,
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    // shouldUnregister: true,
    defaultValues: { ...defaultPatientListConfigurationData, ...values },
  });

  const { setError, handleSubmit, formState, setValue, reset, register } =
    formMethods;

  const localizedName = useMemo(() => {
    if (configurationQuery.data) {
      return i18nGlobal.t(configurationQuery.data.name as any);
    }
  }, [configurationQuery.data]);

  const localizedItem = useMemo(() => {
    if (configurationQuery.data) {
      return {
        ...configurationQuery.data,
        name: localizedName ?? configurationQuery.data.name,
      };
    }
  }, [localizedName, configurationQuery.data]);

  const isNameEditingDisabled = useMemo(() => {
    if (!configurationQuery.data) {
      return false;
    }
    return (
      configurationQuery.data.isSystem ||
      configurationQuery.data.name ===
        'patient_list_configuration.names.default_filter'
    );
  }, [configurationQuery.data]);

  const resetConfiguration = useCallback(() => {
    if (localizedItem) {
      reset(localizedItem);
    }
  }, [localizedItem]);

  useEffect(() => {
    if (localizedItem && !values) {
      resetConfiguration();
    }
  }, [localizedItem]);

  const submitForm = useErrorHandler(
    useCallback(async (data: PatientListConfigurationData) => {
      try {
        data.lastActivity = data.lastActivity ? data.lastActivity : null;
        data.lastVisit = data.lastVisit ? data.lastVisit : null;
        await tryCompleteForm(formType, formId, async () => {
          if (configurationId) {
            await QueryFactory.PatientListConfigurationClient.editPatientListConfiguration(
              configurationId,
              data,
            );

            await queryClient.invalidateQueries({
              queryKey:
                QueryFactory.PatientListConfigurationQuery.getPatientListConfigurationQueryKey(
                  configurationId,
                ),
            });

            await queryClient.invalidateQueries({
              queryKey:
                QueryFactory.PatientListConfigurationQuery.getPatientListConfigurationsQueryKey(),
            });
          } else {
            const createDto = data as any;
            const createData =
              await QueryFactory.PatientListConfigurationClient.createPatientListConfiguration(
                createDto,
              );

            await queryClient.invalidateQueries({
              queryKey:
                QueryFactory.PatientListConfigurationQuery.getPatientListConfigurationsQueryKey(),
            });

            return createData.id;
          }
        });
      } catch (e) {
        const errorDetails = handleSubmitFormError(e, setError);
        if (errorDetails.overallServerError) {
          await modals.showError({ text: errorDetails.overallServerError });
        }
      }
    }, []),
    setError,
  );

  const removeConfiguration = useCallback(async () => {
    if (!configurationId) {
      return;
    }
    const isConfirmed = await modals.showConfirm({
      title: i18n.t('delete.confirm_title'),
      text: i18n.t('delete.confirm_text'),
      okButtonText: i18n.t('delete.confirm_ok_text'),
    });
    if (isConfirmed) {
      loadBlocking(formId, i18n.t('delete.loading'), async () => {
        try {
          await tryCompleteForm(formType, formId, async () => {
            await QueryFactory.PatientListConfigurationClient.removePatientListConfiguration(
              configurationId,
            );
            await queryClient.invalidateQueries({
              queryKey:
                QueryFactory.PatientListConfigurationQuery.getPatientListConfigurationsQueryKey(),
            });
          });
        } catch (err) {
          await modals.showError({ text: convertToErrorString(err) });
        }
      }).catch(console.error);
    }
  }, [formType, formId, tryCompleteForm]);

  return (
    <form
      onSubmit={handleSubmit(submitForm.handler)}
      data-test-id="patientListConfigurationPopup"
    >
      <div className={styles.container}>
        <div className={styles.sections}>
          <SettingsBlock
            blockId={'general'}
            blockTitle={i18n.t('blocks.general')}
          >
            <PatientListGeneralSettings
              formMethods={formMethods}
              isDisabled={isNameEditingDisabled}
            />
          </SettingsBlock>
          <SettingsBlock
            blockId={'filter_settings'}
            blockTitle={i18n.t('blocks.filter_settings')}
          >
            <PatientListFilterSettings formMethods={formMethods} />
          </SettingsBlock>
        </div>
      </div>
      <div className={styles.submitContainer}>
        <div className={styles.buttonGroup}>
          {localizedItem && (
            <>
              {!localizedItem.isSystem && (
                <Button
                  title={i18n.t('delete.button_name')}
                  color={ButtonColor.Danger}
                  iconComponent={<TrashIcon />}
                  onClick={removeConfiguration}
                />
              )}
              <Button
                title={i18n.t('reset.button_name')}
                color={ButtonColor.Primary}
                iconComponent={<ResetIcon />}
                onClick={resetConfiguration}
              />
            </>
          )}
        </div>
        <div className={styles.bottomControls}>
          {claims.permissions.manageSharedPatientListConfigurations && (
            <CheckBox
              {...register('isShared')}
              title={i18n.t('is_shared')}
              disabled={localizedItem?.isSystem}
            />
          )}
          <SubmitButton
            submitting={formState.isSubmitting}
            className={styles.submitButton}
            title={i18n.t('submit.save')}
          />
        </div>
      </div>
    </form>
  );
};
